import { isNumberUnit } from '@exodus/currency'

import { getServer } from '../../exodus-eth-server/index.js'
import { estimateGasLimit } from '../../gas-estimation.js'
import { fromHexToBigInt } from '../../number-utils.js'
import { createWatchTx as defaultCreateWatch } from '../../watch-tx.js'
import { stakingProviderClientFactory } from '../staking-provider-client.js'
import { EthereumStaking } from './api.js'

const extraGasLimit = 20_000 // extra gas Limit to prevent tx failing if something change on pool state (till tx is in mempool)

export function createEthereumStakingService({
  asset,
  assetClientInterface,
  createWatchTx = defaultCreateWatch,
}) {
  const staking = new EthereumStaking(asset)
  const stakingProvider = stakingProviderClientFactory()
  const minAmount = staking.minAmount

  function amountToCurrency({ asset, amount }) {
    return isNumberUnit(amount) ? amount : asset.currency.parse(amount)
  }

  async function delegate({ walletAccount, amount } = Object.create(null)) {
    const address = await assetClientInterface.getReceiveAddress({
      assetName: asset.name,
      walletAccount,
    })
    const delegatorAddress = address.toLowerCase()
    amount = amountToCurrency({ asset, amount })

    const { to, data } = await staking.stake({
      amount,
    })

    const { gasPrice, gasLimit } = await estimateTxFee(delegatorAddress, to, amount, data)

    console.log(
      `delegator address ${delegatorAddress} staking ${amount.toDefaultString({
        unit: true,
      })}`
    )
    const txId = await prepareAndSendTx({
      asset,
      walletAccount,
      to,
      amount,
      txData: data,
      gasPrice,
      gasLimit,
    })

    // Goerli is not supported
    if (asset.name === 'ethereum')
      await stakingProvider.notifyStaking({
        txId,
        asset: asset.name,
        delegator: delegatorAddress,
        amount: amount.toBaseString(),
      })

    return txId
  }

  async function getUndelegatePendingData({
    delegatorAddress,
    resquestedAmount,
    pendingAmount,
    minAmount,
  }) {
    const leftOver = pendingAmount.sub(resquestedAmount)

    if (leftOver.isPositive && leftOver.lt(minAmount)) {
      throw new Error(`Pending balance less than min stake amount ${minAmount}`)
    }

    const inactiveAmountToUnstake = pendingAmount.lte(resquestedAmount)
      ? pendingAmount
      : resquestedAmount
    const { to, data } = await staking.unstakePending({
      address: delegatorAddress,
      amount: inactiveAmountToUnstake,
    })

    const feeData = await estimateTxFee(delegatorAddress, to, null, data)

    return { to, txData: data, ...feeData }
  }

  async function getUndelegateData({ delegatorAddress, resquestedAmount, pendingAmount }) {
    const canUnstake = resquestedAmount.gt(pendingAmount)

    if (!canUnstake) {
      console.warn('UnstakePending covered requested unstake. Nothing to unstake from validator')
      return Object.create(null)
    }

    const activeAmountToUnstake = resquestedAmount.sub(pendingAmount)
    const { to, data } = await staking.unstake({
      address: delegatorAddress,
      amount: activeAmountToUnstake,
    })

    const feeData = await estimateTxFee(delegatorAddress, to, null, data)

    return { to, txData: data, ...feeData }
  }

  /**
   * Unstaking may involve 2 txs; unstaking from pending and unstaking from validator.
   * Fee estimation depends on the executed txs. Can be both.
   * @returns total undelegete fee
   */
  async function estimateUndelegate({ walletAccount, amount: resquestedAmount }) {
    const address = await assetClientInterface.getReceiveAddress({
      assetName: asset.name,
      walletAccount,
    })
    const delegatorAddress = address.toLowerCase()
    const pendingAmount = await staking.pendingBalanceOf(delegatorAddress)

    let undelegatePendingFee = asset.currency.ZERO

    if (pendingAmount.isPositive) {
      try {
        // try to estimate unstakePending
        const { fee } = await getUndelegatePendingData({
          delegatorAddress,
          resquestedAmount,
          pendingAmount,
          minAmount,
        })
        undelegatePendingFee = fee
      } catch (err) {
        // useful to debug fee calculation
        console.warn('ETH unstake pending estimation failed, continuing with unstake', err)
      }
    }

    const { fee: undelegateFee = asset.currency.ZERO } = await getUndelegateData({
      delegatorAddress,
      resquestedAmount,
      pendingAmount,
      minAmount,
    })

    return undelegatePendingFee.add(undelegateFee)
  }

  async function undelegate({ walletAccount, amount } = Object.create(null)) {
    /*
    unstakePending balance (not yet in validator) + unstake balance (in validator)
    1. give priority to unstakePending (based on the amount)
    2. unstake amount in validator.
    */
    const resquestedAmount = amountToCurrency({ asset, amount })

    const address = await assetClientInterface.getReceiveAddress({
      assetName: asset.name,
      walletAccount,
    })
    const delegatorAddress = address.toLowerCase()
    const pendingAmount = await staking.pendingBalanceOf(delegatorAddress)

    console.log(
      `delegator address ${delegatorAddress} unstaking ${resquestedAmount.toDefaultString({
        unit: true,
      })} - pending amount: ${pendingAmount.toDefaultString({ unit: true })}`
    )

    let txId
    if (pendingAmount.isPositive) {
      const undelegatePendingData = await getUndelegatePendingData({
        delegatorAddress,
        resquestedAmount,
        pendingAmount,
        minAmount,
      })

      txId = await prepareAndSendTx({
        asset,
        walletAccount,
        waitForConfirmation: true, // wait for tx confirmation in the client
        ...undelegatePendingData,
      })
    }

    // may need also to unstake
    const undelegateData = await getUndelegateData({
      delegatorAddress,
      resquestedAmount,
      pendingAmount,
    })

    if (undelegateData.fee) {
      txId = await prepareAndSendTx({
        asset,
        walletAccount,
        ...undelegateData,
      })
    }

    // Testnet assets do not support delegations tracking
    if (txId && asset.name === 'ethereum')
      await stakingProvider.notifyUnstaking({
        txId,
        asset: asset.name,
        delegator: delegatorAddress,
        amount: resquestedAmount.toBaseString(),
      })

    return txId
  }

  async function claimUndelegatedBalance({ walletAccount } = Object.create(null)) {
    // claim withdrawable balance (of a previous unstake)
    const address = await assetClientInterface.getReceiveAddress({
      assetName: asset.name,
      walletAccount,
    })
    const delegatorAddress = address.toLowerCase()

    const withdrawRequest = await staking.claimWithdrawRequest({
      address: delegatorAddress,
    })
    if (withdrawRequest) {
      const { to, data } = withdrawRequest
      const { gasPrice, gasLimit } = await estimateTxFee(delegatorAddress, to, null, data)
      return prepareAndSendTx({
        asset,
        walletAccount,
        to,
        txData: data,
        gasPrice,
        gasLimit,
      })
    }
  }

  async function estimateDelegateOperation({ walletAccount, operation, args }) {
    const requestedAmount = args.amount
      ? amountToCurrency({ asset, amount: args.amount })
      : asset.currency.ZERO

    if (operation === 'undelegate') {
      return estimateUndelegate({ walletAccount, amount: requestedAmount })
    }

    const address = await assetClientInterface.getReceiveAddress({
      assetName: asset.name,
      walletAccount,
    })
    const delegatorAddress = address.toLowerCase()

    const NAMING_MAP = {
      delegate: 'stake',
      undelegate: 'unstake',
      claimUndelegatedBalance: 'claimWithdrawRequest',
    }

    const delegateOperation = staking[NAMING_MAP[operation]].bind(staking)

    if (!delegateOperation) {
      throw new Error('Invalid staking operation')
    }

    const { amount, data } = await delegateOperation({ ...args, amount: requestedAmount })
    const toAddress =
      operation === 'claimUndelegatedBalance' ? staking.accountingAddress : staking.poolAddress

    const { fee } = await estimateTxFee(delegatorAddress, toAddress, amount, data)

    return fee
  }

  async function estimateTxFee(from, to, amount, txInput, gasPrice = '0x0') {
    amount = amount || asset.currency.ZERO

    const gasLimit = await estimateGasLimit(
      asset,
      from.toLowerCase(),
      to.toLowerCase(),
      amount, // staking contracts does not always require ETH amount to interact with
      txInput,
      gasPrice
    )

    if (gasPrice === '0x0') {
      gasPrice = await getServer(asset).gasPrice()
    }

    gasPrice = fromHexToBigInt(gasPrice)
    const fee = gasPrice * BigInt(gasLimit + extraGasLimit)

    return {
      gasLimit,
      gasPrice: asset.currency.baseUnit(gasPrice.toString()),
      fee: asset.currency.baseUnit(fee.toString()),
    }
  }

  function getMinAmount() {
    return staking.minAmount
  }

  async function prepareAndSendTx(
    {
      asset,
      walletAccount,
      to,
      amount,
      txData: txInput,
      gasPrice,
      gasLimit,
      waitForConfirmation = false,
    } = Object.create(null)
  ) {
    const sendTxArgs = {
      asset,
      walletAccount,
      address: to,
      amount: amount || asset.currency.ZERO,
      shouldLog: true,
      options: {
        shouldLog: true,
        txInput,
        gasPrice,
        gasLimit,
      },
    }

    const { txId } = await asset.baseAsset.api.sendTx(sendTxArgs)

    const baseAsset = asset.baseAsset
    if (waitForConfirmation) {
      const getTxLog = (...args) => assetClientInterface.getTxLog(...args)
      const watchTx = createWatchTx({
        walletAccount,
        getTxLog,
        baseAsset,
      })
      await watchTx(txId)
    }

    return txId
  }

  return {
    delegate,
    undelegate,
    claimUndelegatedBalance,
    getEthereumStakingInfo,
    estimateDelegateOperation,
    getMinAmount,
  }
}

export async function getEthereumStakingInfo({ address, asset, server }) {
  const { currency } = asset
  const delegator = address.toLowerCase()
  const staking = new EthereumStaking(asset, undefined, server)

  const [
    activeStakedBalance,
    pendingBalance,
    pendingDepositedBalance,
    withdrawRequest,
    rewardsBalance,
  ] = await Promise.all([
    staking.autocompoundBalanceOf(delegator),
    staking.pendingBalanceOf(delegator),
    staking.pendingDepositedBalanceOf(delegator),
    staking.withdrawRequest(delegator),
    staking.getLiquidRewards(delegator),
  ])

  const delegatedBalance = activeStakedBalance.add(pendingBalance).add(pendingDepositedBalance)
  const minDelegateAmount = currency.defaultUnit(0.1)

  const {
    requested,
    canSendClaimRequest,
    /*, readyForClaim */
  } = withdrawRequest

  const isDelegating = !delegatedBalance.isZero || !requested.isZero
  const isUndelegateInProgress = !requested.isZero && !canSendClaimRequest

  const canClaimUndelegatedBalance = canSendClaimRequest

  const unclaimedUndelegatedBalance = requested

  return {
    rewardsBalance,
    isDelegating,
    activeStakedBalance, // balance staked into the validator
    delegatedBalance, // balance staked into the validator + pending
    pendingBalance, // balance waiting to be staked
    pendingDepositedBalance, // balance deposited into validator but not active yet
    minDelegateAmount,
    unclaimedUndelegatedBalance,
    canClaimUndelegatedBalance,
    isUndelegateInProgress,
  }
}
